(() => {
  const key = Symbol.for('tl.header.navigation.initialize');
  if (window[key]) { window[key](document); return; }
  const instances = new WeakMap();
  class HeaderNavigation {
    constructor(root) {
      this.root = root;
      this.events = new AbortController();
      this.animations = new Map();
      this.desktop = [...root.querySelectorAll('[data-desktop-category]')];
      this.drawer = root.querySelector('.tl-mobile-menu');
      this.drawerPanel = this.drawer.querySelector('[data-mobile-drawer]');
      this.opener = this.drawer.querySelector(':scope > summary');
      this.categories = [...root.querySelectorAll('[data-mobile-category]')];
      this.mobile = matchMedia('(max-width:989px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion:reduce)');
      this.drawerExpanded = this.drawer.open;
      this.drawer.dataset.drawerExpanded = String(this.drawerExpanded);
      this.drawer.dataset.drawerState = this.drawerExpanded ? 'open' : 'closed';
      this.drawerPanel.inert = !this.drawerExpanded;
      const signal = this.events.signal;
      this.position();
      this.opener.addEventListener('click', event => {
        event.preventDefault();
        this.drawerExpanded ? this.closeDrawer(true) : this.openDrawer();
      }, { signal });
      this.drawer.addEventListener('toggle', () => {
        if (this.drawer.open && !this.ownsScrollLock) this.openDrawer();
        if (!this.drawer.open && this.ownsScrollLock) this.closeDrawer(false, true);
        this.opener.setAttribute('aria-expanded', String(this.drawerExpanded));
      }, { signal });
      for (const details of this.desktop) {
        const summary = details.querySelector(':scope > summary');
        details.addEventListener('mouseenter', () => { if (!this.mobile.matches && matchMedia('(hover:hover)').matches) this.openDesktop(details); }, { signal });
        details.addEventListener('mouseleave', () => { if (!details.contains(document.activeElement)) details.open = false; }, { signal });
        summary.addEventListener('click', event => {
          if (event.target.closest('a')) return;
          event.preventDefault();
          details.open ? details.open = false : this.openDesktop(details);
        }, { signal });
        details.addEventListener('focusout', () => {
          queueMicrotask(() => { if (!details.contains(document.activeElement) && !details.matches(':hover')) details.open = false; });
        }, { signal });
        details.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(details.open)), { signal });
      }
      for (const details of this.categories) {
        const summary = details.querySelector(':scope > summary'), content = details.querySelector(':scope > .tl-mobile-category__content');
        details.dataset.expanded = String(details.open);
        content.inert = !details.open;
        summary.setAttribute('aria-expanded', String(details.open));
        content.style.maxHeight = details.open ? content.firstElementChild.scrollHeight + 'px' : '0px';
        summary.addEventListener('click', event => {
          event.preventDefault();
          this.setCategory(details, details.dataset.expanded !== 'true');
        }, { signal });
      }
      root.addEventListener('keydown', event => this.keydown(event), { signal });
      document.addEventListener('pointerdown', event => { if (!root.contains(event.target)) { this.closeDesktop(); this.closeDrawer(false); } }, { signal });
      document.addEventListener('tl:header:close-drawer', () => { this.closeDesktop(); this.closeDrawer(false, true); }, { signal });
      this.mobile.addEventListener('change', () => {
        const wasInside = this.drawer.contains(document.activeElement);
        this.closeDesktop();
        this.closeDrawer(false, true);
        this.position();
        if (!this.mobile.matches && wasInside) root.querySelector('.tl-logo')?.focus({ preventScroll: true });
      }, { signal });
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches && this.drawerAnimation) this.finishDrawer(this.drawerExpanded);
      }, { signal });
      window.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.position(); });
      }, { signal, passive: true });
      this.observer = new ResizeObserver(() => {
        this.position();
        for (const details of this.categories) if (details.dataset.expanded === 'true' && !this.animations.has(details)) details.querySelector('.tl-mobile-category__content').style.maxHeight = details.querySelector('.tl-mobile-category__content>div').scrollHeight + 'px';
      });
      this.observer.observe(root);
      for (const details of this.categories) this.observer.observe(details.querySelector('.tl-mobile-category__content>div'));
      root.dataset.navigationReady = 'true';
    }
    position() { this.root.style.setProperty('--tl-nav-top', Math.max(0, this.root.getBoundingClientRect().bottom) + 'px'); }
    openDesktop(details) {
      this.position();
      this.desktop.forEach(item => item.open = item === details);
      details.querySelectorAll('img').forEach(image => image.loading = 'eager');
    }
    closeDesktop() { this.desktop.forEach(details => details.open = false); }
    openDrawer() {
      if (!this.mobile.matches) return;
      document.dispatchEvent(new CustomEvent('tl:header:open-drawer'));
      this.closeDesktop();
      if (!this.ownsScrollLock) {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.ownsScrollLock = true;
      }
      this.position();
      const wasOpen = this.drawer.open;
      const state = this.prepareDrawerAnimation(true);
      this.drawerExpanded = true;
      this.drawer.open = true;
      this.drawer.dataset.drawerState = 'opening';
      this.drawerPanel.inert = false;
      this.opener.setAttribute('aria-expanded', 'true');
      const reveal = () => {
        if (this.drawerAnimation !== state) return;
        state.frame = 0;
        this.drawer.dataset.drawerExpanded = 'true';
        if (this.reducedMotion.matches) this.finishDrawer(true);
        else this.armDrawerAnimation(state);
      };
      if (wasOpen || this.reducedMotion.matches) reveal();
      else {
        // Seed the closed transform before starting the measured source slide.
        this.drawerPanel.getBoundingClientRect();
        state.frame = requestAnimationFrame(reveal);
      }
    }
    cancelDrawerAnimation() {
      const state = this.drawerAnimation;
      if (!state) return;
      clearTimeout(state.timer);
      cancelAnimationFrame(state.frame);
      state.events.abort();
      this.drawerAnimation = undefined;
    }
    prepareDrawerAnimation(open) {
      this.cancelDrawerAnimation();
      const state = { open, events: new AbortController() };
      this.drawerAnimation = state;
      this.drawerPanel.addEventListener('transitionend', event => {
        if (event.target === this.drawerPanel && event.propertyName === 'transform' && this.drawerAnimation === state) this.finishDrawer(open);
      }, { signal: state.events.signal });
      return state;
    }
    armDrawerAnimation(state) {
      // A fallback only cleans up when a transition cannot emit its end event.
      state.timer = setTimeout(() => {
        if (this.drawerAnimation === state) this.finishDrawer(state.open);
      }, 240);
    }
    finishDrawer(open) {
      this.cancelDrawerAnimation();
      this.drawerExpanded = open;
      this.drawer.dataset.drawerExpanded = String(open);
      this.drawer.dataset.drawerState = open ? 'open' : 'closed';
      this.drawerPanel.inert = !open;
      this.drawer.open = open;
      this.opener.setAttribute('aria-expanded', String(open));
      if (!open && this.ownsScrollLock) {
        document.body.style.overflow = this.previousOverflow;
        this.ownsScrollLock = false;
      }
    }
    closeDrawer(returnFocus, immediate = false) {
      const wasOpen = this.drawer.open;
      if (returnFocus && wasOpen) this.opener.focus({ preventScroll: true });
      if (!this.drawerExpanded && this.drawerAnimation && !immediate) return;
      const hidden = !wasOpen || this.drawerPanel.getBoundingClientRect().right <= 0.5;
      this.drawerExpanded = false;
      this.drawer.dataset.drawerExpanded = 'false';
      this.drawerPanel.inert = true;
      this.opener.setAttribute('aria-expanded', 'false');
      if (immediate || hidden || this.reducedMotion.matches || !this.mobile.matches) {
        this.finishDrawer(false);
      } else {
        const state = this.prepareDrawerAnimation(false);
        this.drawer.dataset.drawerState = 'closing';
        this.armDrawerAnimation(state);
      }
    }
    setCategory(details, open) {
      const old = this.animations.get(details);
      clearTimeout(old?.timer);
      cancelAnimationFrame(old?.frame);
      const content = details.querySelector(':scope > .tl-mobile-category__content'), summary = details.querySelector(':scope > summary');
      details.dataset.expanded = String(open);
      content.inert = !open;
      summary.setAttribute('aria-expanded', String(open));
      if (open) details.open = true;
      const startHeight = content.getBoundingClientRect().height;
      content.style.maxHeight = startHeight + 'px';
      const state = {};
      this.animations.set(details, state);
      state.frame = requestAnimationFrame(() => {
        state.frame = 0;
        content.style.maxHeight = open ? content.firstElementChild.scrollHeight + 'px' : '0px';
        state.timer = setTimeout(() => {
          if (!open) details.open = false;
          this.animations.delete(details);
          if (open) content.style.maxHeight = content.firstElementChild.scrollHeight + 'px';
        }, matchMedia('(prefers-reduced-motion:reduce)').matches ? 0 : 320);
      });
    }
    keydown(event) {
      if (event.key === 'Escape') {
        const category = event.target.closest('[data-mobile-category]');
        const desktop = this.desktop.find(d => d.open && d.contains(event.target)) || this.desktop.find(d => d.open);
        if (category?.dataset.expanded === 'true') {
          this.setCategory(category, false);
          category.querySelector('summary').focus({ preventScroll: true });
        } else if (this.drawerExpanded) this.closeDrawer(true);
        else if (desktop) { desktop.open = false; desktop.querySelector('summary').focus({ preventScroll: true }); }
        else return;
        event.preventDefault();
        event.stopPropagation();
      } else if (event.key === 'Tab' && this.drawerExpanded) {
        const focusable = [this.opener, ...this.drawer.querySelector('[data-mobile-drawer]').querySelectorAll('a[href],summary,button')].filter(e => !e.closest('[inert]') && e.checkVisibility({ checkVisibilityCSS: true }) && !e.disabled && e.tabIndex >= 0);
        const first = focusable[0], last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    destroy() {
      this.closeDrawer(false, true);
      this.closeDesktop();
      this.events.abort();
      this.observer.disconnect();
      cancelAnimationFrame(this.frame);
      for (const state of this.animations.values()) { clearTimeout(state.timer); cancelAnimationFrame(state.frame); }
      this.animations.clear();
      delete this.root.dataset.navigationReady;
    }
  }
  function initialize(root = document) {
    root.querySelectorAll('[data-tl-header]').forEach(node => { if (!instances.has(node)) instances.set(node, new HeaderNavigation(node)); });
  }
  window[key] = initialize;
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-tl-header]').forEach(node => { instances.get(node)?.destroy(); instances.delete(node); }));
})();
